/**
 * The media a post was published with.
 *
 * Media selection helper routines for the post composer. These helpers are
 * the single source of truth for resolving published media and preparing it
 * for submission.
 */
import { POST_IMAGE_LAYOUT, POST_MEDIA_TYPE } from '../constants';
import type { FeedPost, MediaFile, PostImageLayout } from '../types';
import { resolveLayoutForAlbum } from './postImageLayout';

/** The media half of the composer state. */
export interface PostMediaSelection {
  media: MediaFile | null;
  mediaFiles: MediaFile[];
  imageLayout: PostImageLayout | null;
}

const NO_MEDIA: PostMediaSelection = {
  media: null,
  mediaFiles: [],
  imageLayout: null,
};

/** The API has no GIF flag on the post itself — the URL is the only signal. */
const isGifUri = (uri: string): boolean => {
  const value = uri.toLowerCase();
  return value.endsWith('.gif') || value.includes('giphy.com');
};

/** Post media arrives as a bare URL, as `{ uri }`, or as a bundled asset. */
const readUri = (source: unknown): string => {
  if (typeof source === 'string') return source.trim();
  if (source && typeof source === 'object' && 'uri' in source) {
    const { uri } = source as { uri?: unknown };
    return typeof uri === 'string' ? uri.trim() : '';
  }
  return '';
};

export const resolvePublishedPostMedia = (
  post?: FeedPost | null,
): PostMediaSelection => {
  if (!post) return NO_MEDIA;

  const videoUri = readUri(post.video);
  if (videoUri) {
    return {
      media: {
        uri: videoUri,
        mediaType: POST_MEDIA_TYPE.VIDEO,
        width: post.videoWidth ?? post.video?.width,
        height: post.videoHeight ?? post.video?.height,
        type: 'video/mp4',
      },
      mediaFiles: [],
      imageLayout: null,
    };
  }

  const album = Array.isArray(post.images)
    ? post.images.map(readUri).filter(Boolean)
    : [];

  if (album.length > 1) {
    const files: MediaFile[] = album.map(uri => ({
      uri,
      mediaType: POST_MEDIA_TYPE.IMAGE,
      type: 'image/jpeg',
    }));
    return {
      media: files[0],
      mediaFiles: files,
      // The layout the post carries wins. Guessing from the count alone turns a
      // three-photo gallery into an incomplete Comparison4, which then fails
      // validation and blocks an otherwise text-only edit.
      imageLayout: post.imageLayout ?? resolveLayoutForAlbum(files.length, null),
    };
  }

  const imageUri = readUri(post.image) || album[0] || '';
  if (!imageUri) return NO_MEDIA;

  const gif = isGifUri(imageUri);
  const image: MediaFile = {
    uri: imageUri,
    mediaType: gif ? POST_MEDIA_TYPE.GIF : POST_MEDIA_TYPE.IMAGE,
    type: gif ? 'image/gif' : 'image/jpeg',
  };
  return {
    media: image,
    mediaFiles: [image],
    imageLayout: POST_IMAGE_LAYOUT.SINGLE,
  };
};

export interface SubmittedPostMediaInput {
  isEditing: boolean;
  /** The post being edited, as it was published. */
  publishedPost: FeedPost | null;
  /** What the composer currently holds. */
  composerMedia: PostMediaSelection;
}

/**
 * The media a submit may carry.
 *
 * A published post's media is frozen: its likes and comments belong to what
 * people actually saw, so an edit may only change the text. An edit therefore
 * sends the media resolved from the published post and ignores the composer
 * entirely — whether the composer holds a replacement, or nothing because the
 * user cleared it. Hiding the composer's media buttons is presentation; this is
 * the rule.
 */
export const resolveSubmittedPostMedia = ({
  isEditing,
  publishedPost,
  composerMedia,
}: SubmittedPostMediaInput): PostMediaSelection =>
  isEditing ? resolvePublishedPostMedia(publishedPost) : composerMedia;
